import { useNavigate } from "react-router";
import { getUser, resetUser, userIsInit, userState } from "./store/userState";
import { useEffect } from "react";
import useIsAuthWrapper from "./hooks/useIsAuthWrapper";
import { toast, Toaster } from "sonner";
import { getListProducts, listProductsState } from "./store/listProductsState";
import mixpanel from "mixpanel-browser";
import {
  currentSubscriptionState,
  getCurrentSubscription,
} from "./store/currentSubscriptionState";
import { getUserRestaurants } from "./store/userRestaurantsState";
import { getSearchHistory } from "./store/searchHistoryState";
import LogRocket from "logrocket";
import "aws-amplify/auth/enable-oauth-listener";
import { Hub } from "aws-amplify/utils";
import { getUserFavoriteList } from "./store/userFavoriteListState";
import { restrictedFeatureState } from "./store/restrictedFeatureState";
import RestrictedFreeAction from "./components/RestrictedFreeAction";
import {
  paymentActionState,
  setPaymentAction,
  setPriceId,
} from "./store/paymentActionState";
import Checkout from "./components/dialogs/Checkout";
import RestrictedExplorerAction from "./components/RestrictedExplorerAction";
import { getEntitlements } from "./store/entitlementsState";
import { generateClient } from "aws-amplify/api";
import { onNewExport } from "./graphql/subscriptions";
import type { Subscription } from "rxjs";
import ModalFreeTrial from "./components/ModalFreeTrial";
import { setPlan } from "./store/userPlanState";
import { getRemainingKrunshCredits } from "./store/remainingKrunshCreditsState";

const client = generateClient();

function App() {
  const isInit = userIsInit();
  const navigate = useNavigate();
  const isAuthWrapperRoute = useIsAuthWrapper();

  const { data: userData, error: userError, isLoading } = userState();
  const { data: planUser, isLoading: planUserIsloading } =
    currentSubscriptionState();
  const { data: listProducts } = listProductsState();
  const {
    actionTriggerNeedCore,
    actiontriggerNeedNetwork,
    actionTriggerModalTrialModal,
  } = restrictedFeatureState();
  const { triggerPaymentAction } = paymentActionState();

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    const litenerHub = Hub.listen("auth", (data) => {
      const { payload } = data;
      const { event } = payload;

      if (event === "tokenRefresh_failure") {
        resetUser();
        navigate("/explorer/sign-in");
      }
    });

    return () => {
      litenerHub();
    };
  }, [userData]);

  useEffect(() => {
    if (userData) {
      getRemainingKrunshCredits();
      getListProducts();
      getCurrentSubscription();
      getUserRestaurants();
      getSearchHistory();
      getUserFavoriteList();
      getEntitlements();

      LogRocket.identify(userData.email, {
        name: userData.name,
        email: userData.email,
      });
    }
  }, [userData]);

  useEffect(() => {
    if (planUser && listProducts && userData) {
      const product = listProducts
        .filter((it) => it)
        .find(({ prices }) =>
          prices.find((price) => price?.id === planUser.priceId),
        );

      if (product) {
        const findPrice = product.prices.findIndex(
          (price) => price && price.id === planUser.priceId,
        );

        if (findPrice != -1) {
          const price = product.prices[findPrice];

          if (price) {
            mixpanel.people.set({
              plan: product.name,
            });
            // @ts-ignore
            cioanalytics.identify(userData.email, {
              plan: product.name,
            });

            setPlan(product);
          }
        }
      }

      if (!product) {
        mixpanel.people.set({
          plan: "starter",
        });
        // @ts-ignore
        cioanalytics.identify(userData.email, {
          plan: "starter",
        });
      }
    }

    if (!planUser && !planUserIsloading && userData) {
      mixpanel.people.set({
        plan: "starter",
      });
      // @ts-ignore
      cioanalytics.identify(userData.email, {
        plan: "starter",
      });
    }
  }, [planUser, listProducts, planUserIsloading, userData]);

  useEffect(() => {
    if (
      (userError || !userData) &&
      !isAuthWrapperRoute &&
      !isLoading &&
      !isInit
    ) {
      navigate("/explorer/sign-up");
    }

    if (
      !userError &&
      !isLoading &&
      !isInit &&
      !isAuthWrapperRoute &&
      !userData?.["custom:isOnboarded"]
    ) {
      navigate("/explorer/onboarding");
    }
  }, [userError, userData, isLoading, isAuthWrapperRoute, isInit]);

  useEffect(() => {
    let getGridExport: Subscription;
    if (userData) {
      getGridExport = client
        .graphql({
          query: onNewExport,
          variables: { username: userData.username },
        })
        .subscribe({
          next: ({ data }) => {
            const url = data.onNewExport.url;

            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "export_grid_builder.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success(
              "Votre export est prêt ! Le téléchargement va commencer automatiquement.",
            );
          },
          error: (error) => {
            toast.error("Une erreur est survenue, veuillez réessayer.");
            console.log("Erreur getting CSV ", error);
          },
        });
    }

    return () => {
      if (getGridExport) getGridExport.unsubscribe();
    };
  }, [userData]);

  return (
    <>
      <Toaster position="top-right" style={{ top: 100, right: 24 }} />

      {actionTriggerNeedCore && <RestrictedFreeAction />}
      {actiontriggerNeedNetwork && <RestrictedExplorerAction />}
      {actionTriggerModalTrialModal && <ModalFreeTrial />}
      {triggerPaymentAction &&
        ["pro", "creditPack"].includes(triggerPaymentAction) && (
          <Checkout
            openCheckout={true}
            onClose={() => {
              setPaymentAction(undefined);
              setPriceId(undefined);
            }}
          />
        )}
    </>
  );
}

export default App;
